import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import '../styles/MyBusPage.css'

const getDayNoteKey = id => `tickmypax-day-note-${id}`

function shouldIgnoreMyBusRecord(record) {
  const tourType = String(record?.tourType || '').trim().toLowerCase()
  return tourType.includes('driver and bus reg')
}
const previewRecords = [
  { id: 'p1', tourDate: new Date().toISOString(), surname: 'Murphy', firstName: 'Anna', pax: 2, checkedInBy: 'Tony', notes: '' },
  { id: 'p2', tourDate: new Date().toISOString(), surname: 'Byrne', firstName: 'Liam', pax: 4, checkedInBy: 'Tony', notes: 'Front seats requested' },
  { id: 'p3', tourDate: new Date().toISOString(), surname: 'Smith', firstName: 'Eva', pax: 1, checkedInBy: 'Sarah', notes: '' },
  { id: 'p4', tourDate: new Date().toISOString(), surname: 'Kelly', firstName: 'Mark', pax: 3, checkedInBy: 'Sarah', notes: 'Running 5 mins late' }
]

function MyBusPage({ previewMode = false }) {
  const [records, setRecords] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [selectedGuide, setSelectedGuide] = useState('')
  const [noteDrafts, setNoteDrafts] = useState({})
  const [recountChecks, setRecountChecks] = useState({})

  const recordsApiUrl = `${process.env.REACT_APP_API_URL}/api/records`

  useEffect(() => {
    if (previewMode) {
      setRecords(previewRecords)
      setLoading(false)
      return
    }

    fetch(recordsApiUrl, {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    })
      .then(res => {
        if (!res.ok) throw new Error(`HTTP error: ${res.status}`)
        return res.json()
      })
      .then(data => {
        const filteredData = data.filter(record => !shouldIgnoreMyBusRecord(record))
        setRecords(filteredData)
        setLoading(false)
      })
      .catch(err => {
        setError(err.message)
        setLoading(false)
      })
  }, [previewMode, recordsApiUrl])

  const guideOptions = useMemo(() => {
    return [...new Set(records.map(record => String(record.checkedInBy || '').trim()).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b))
  }, [records])

  useEffect(() => {
    if (!selectedGuide && guideOptions.length) {
      setSelectedGuide(guideOptions[0])
    }
  }, [guideOptions, selectedGuide])

  useEffect(() => {
    const drafts = {}
    records.forEach(record => {
      drafts[record.id] = localStorage.getItem(getDayNoteKey(record.id)) || ''
    })
    setNoteDrafts(drafts)
  }, [records])

  useEffect(() => {
    const nextChecks = {}
    guidePassengers.forEach(passenger => {
      nextChecks[passenger.id] = recountChecks[passenger.id] || false
    })
    setRecountChecks(nextChecks)
  }, [selectedGuide])

  const guidePassengers = useMemo(() => {
    return records
      .filter(record => String(record.checkedInBy || '').trim().toLowerCase() === selectedGuide.trim().toLowerCase())
      .sort((a, b) => {
        const surnameCompare = String(a.surname || '').localeCompare(String(b.surname || ''))
        if (surnameCompare !== 0) return surnameCompare
        return String(a.firstName || '').localeCompare(String(b.firstName || ''))
      })
  }, [records, selectedGuide])

  const totalPax = guidePassengers.reduce((sum, passenger) => sum + Number(passenger.pax || 0), 0)
  const newCount = guidePassengers.reduce((sum, passenger) => {
    return sum + (recountChecks[passenger.id] ? Number(passenger.pax || 0) : 0)
  }, 0)

  const saveDayNote = id => {
    localStorage.setItem(getDayNoteKey(id), noteDrafts[id] || '')
  }

  if (loading) return <div className="loading">Loading guide bus view...</div>
  if (error) return <div className="error">Error: {error}</div>

  return (
    <div className="my-bus-page">
      <div className="my-bus-header">
        <div>
          <p className="my-bus-kicker">Guide loading view</p>
          <h1>My Bus</h1>
          <p className="my-bus-subtle">Pick your name to see the passengers you checked in today and add short day notes.</p>
        </div>
        <Link className="my-bus-back-link" to="/">
          ← Back home
        </Link>
      </div>

      <div className="my-bus-toolbar">
        <label className="my-bus-select-wrap">
          <span>Guide</span>
          <select value={selectedGuide} onChange={e => setSelectedGuide(e.target.value)}>
            {guideOptions.length ? guideOptions.map(guide => (
              <option key={guide} value={guide}>{guide}</option>
            )) : <option value="">No checked-in guides yet</option>}
          </select>
        </label>

        <div className="my-bus-summary-strip">
          <div className="my-bus-summary-card">
            <strong>{selectedGuide || 'No guide selected'}</strong>
            <span>{totalPax}</span>
          </div>
          <div className="my-bus-summary-card my-bus-summary-card-secondary">
            <strong>New count</strong>
            <span>{newCount}</span>
            <button
              type="button"
              className="my-bus-reset-btn"
              onClick={() => {
                const cleared = {}
                guidePassengers.forEach(passenger => {
                  cleared[passenger.id] = false
                })
                setRecountChecks(cleared)
              }}
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      <div className="my-bus-table-wrap">
        <table className="my-bus-table">
          <thead>
            <tr>
              <th>Surname</th>
              <th>First Name</th>
              <th>Pax</th>
              <th className="my-bus-recount-column">Recount</th>
              <th>Day Note</th>
            </tr>
          </thead>
          <tbody>
            {!selectedGuide ? (
              <tr>
                <td colSpan="5">No guide selected yet.</td>
              </tr>
            ) : guidePassengers.length ? guidePassengers.map(passenger => (
              <tr key={passenger.id}>
                <td>{passenger.surname}</td>
                <td>{passenger.firstName}</td>
                <td>{passenger.pax}</td>
                <td>
                  <div className="my-bus-recount-cell">
                    <input
                      type="checkbox"
                      checked={Boolean(recountChecks[passenger.id])}
                      onChange={e => setRecountChecks(prev => ({ ...prev, [passenger.id]: e.target.checked }))}
                    />
                  </div>
                </td>
                <td>
                  <div className="my-bus-note-cell">
                    <textarea
                      rows="2"
                      placeholder="Add short note for today..."
                      value={noteDrafts[passenger.id] || ''}
                      onChange={e => setNoteDrafts(prev => ({ ...prev, [passenger.id]: e.target.value }))}
                    />
                    <button type="button" onClick={() => saveDayNote(passenger.id)}>Save note</button>
                  </div>
                </td>
              </tr>
            )) : (
              <tr>
                <td colSpan="5">No passengers checked in by this guide yet.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default MyBusPage
